import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AssessmentCentralService {
  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    let id = route.params["id"];
    try {
      id = atob(id);
    } catch (e) {
      this.router.navigate(["404"]);
    }
    return this.http.get(`assessment/assignement_instance/${id}/`);
  }

  getAllAssessments() {
    return this.http.get(`assessment/assignement_instance/`);
  }

  getTemplates() {
    return this.http.get(`assessment/assignment_template/`);
  }

  downloadTemplate(id: number) {
    return this.http.get(
      `assessment/assignment_template/assessment_file_download/${id}/`,
      { responseType: "blob" }
    );
  }

  uploadFile(payload: any) {
    return this.http.post(`assessment/assignment_template/`, payload);
  }

  createAssessment(payload: any, moduleName: string = "") {
    return this.http.post(
      `assessment/assignement_instance/?module=${moduleName}`,
      payload
    );
  }

  deleteTemplate(id: any) {
    return this.http.delete(`assessment/assignment_template/` + id);
  }

  getAssessmentById(id: any) {
    return this.http.get(`assessment/assignement_instance/${id}/`);
  }

  submitAnswer(payload: any) {
    return this.http.post(`assessment/assessment_response/`, payload);
  }

  getUsers() {
    return this.http.get(`users/user-list/`);
  }

  uploadEvidence(payload: any) {
    return this.http.post(
      `assessment/assessment_response/upload_evidence/`,
      payload
    );
  }

  deleteAssessment(id: any) {
    return this.http.delete(`assessment/assignement_instance/` + id);
  }

  getMasterForm() {
    return this.http.get(`dynamic-form/?form_name=new_opportunity`);
  }

  getProgram(moduleName: any = "") {
    return this.http.get(`org/programs/?module=${moduleName}`);
  }

  getProject(moduleName: any = "") {
    return this.http.get(`projects/?module=${moduleName}`);
  }

  getVendor() {
    return this.http.get(
      `dynamic-form/?form_name=opportunity_empanelled_vendors`
    );
  }

  fileDownload(instanceId: any, quesId: any, userId: any) {
    return this.http.get(
      `assessment/assessment_response/download_evidence/?instance_id=${instanceId}&question_id=${quesId}&user_id=${userId}`,
      { responseType: "blob" }
    );
  }

  getAssessmentTemplateCategory() {
    return this.http.get("base_value/?type=assessment_category");
  }

  getFilteredAssessmentTemplates(qParam: string) {
    return this.http.get(`assessment/assignment_template/?${qParam}`);
  }

  getFilteredAssessmentCentral(qParam: string) {
    return this.http.get(`assessment/assignement_instance/?${qParam}`);
  }

  getAggregateResponseDetails(assessmentId: any) {
    return this.http.get(
      `assessment/assignement_instance/response-details/${assessmentId}/`
    );
  }

  getAnswerOptions() {
    return this.http.get("base_value/?type=assessment_response_option");
  }

  getOwnersList() {
    return this.http.get("users/");
  }

  getAssessmentsByProject(projectId: any) {
    return this.http.get(
      `assessment/assignement_instance/all/?search=project:${projectId}`
    );
  }

  getCategoriesList(categoryType: any) {
    return this.http.get(`base_value/?type=${categoryType}`);
  }

  getAssesmentCentralNamesList(nameType: any) {
    return this.http.get(`assessment/${nameType}/names/`);
  }

  getGovernanceLightHouseNamesList(nameType: any) {
    return this.http.get(`gov-lighthouse/${nameType}/names/`);
  }

  downloadSampleTemplate() {
    return this.http.get(`assessment/assignment_template/default/`, {
      responseType: "blob",
    });
  }

  createAssessmentTemplateFromJson(payload: any): Observable<any> {
    return this.http.post(
      `assessment/assignment_template/create-from-json/`,
      payload
    );
  }

  updateAssessmentTemplate(templateId: number, payload: any): Observable<any> {
    return this.http.put(
      `assessment/assignment_template/${templateId}/`,
      payload
    );
  }

  getQuestionDocuments(instanceId: number, questionId: number, userId: number) {
    return this.http.get(
      `assessment/assessment-response-documents/for-question/`,
      {
        params: {
          instance_id: instanceId.toString(),
          question_id: questionId.toString(),
          user_id: userId.toString(),
        },
      }
    );
  }

  uploadRequiredDocumentEvidence(formData: any) {
    return this.http.post(
      `assessment/assessment-response-documents/upload/`,
      formData
    );
  }

  deleteRequiredDocumentEvidence(documentId: number) {
    return this.http.delete(
      `assessment/assessment-response-documents/${documentId}/`
    );
  }

  downloadRequiredDocumentEvidence(documentId: number) {
    return this.http.get(
      `assessment/assessment-response-documents/${documentId}/download/`,
      {
        responseType: "blob",
      }
    );
  }

  updateAssessmentStatus(instanceId: number, payload: any): Observable<any> {
    return this.http.patch(
      `assessment/assignement_instance/${instanceId}/`,
      payload
    );
  }

  saveAssessorReview(payload: {
    instance_id: number | string;
    question_id: number | string;
    result: string;
    observation: string;
    risk_type: string | number;
    risk_rating: string;
    comment: string;
  }): Observable<any> {
    return this.http.post(
      `assessment/assessment_response/assessor-review/`,
      payload
    );
  }

  addRequiredDocument(payload: {
    instance: number;
    question: number;
    name: string;
  }) {
    return this.http.post(`assessment/instance-required-documents/`, payload);
  }

  updateRequiredDocument(id: number, name: string) {
    return this.http.patch(`assessment/instance-required-documents/${id}/`, {
      name,
    });
  }

  deleteRequiredDocument(id: number) {
    return this.http.delete(`assessment/instance-required-documents/${id}/`);
  }

  getAssessmentResponseChoices() {
    return this.http.get("base_value/?type=assessment_template_response");
  }

  createManualTemplate(payload: any) {
    return this.http.post(
      `assessment/assignment_template/create-template/`,
      payload
    );
  }
}
